import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useAuthState } from "@atlas/auth";
import { useEffect } from "react";

/**
 * Validate password meets requirements:
 * - At least 8 characters
 * - Contains at least one letter
 * - Contains at least one number or symbol
 */
function validatePassword(password: string): { valid: boolean; message: string } {
  if (password.length < 8) {
    return { valid: false, message: "Password must be at least 8 characters" };
  }

  if (!/[a-zA-Z]/.test(password)) {
    return { valid: false, message: "Password must contain at least one letter" };
  }

  if (!/[0-9!@#$%^&*()_+\-=[\]{}|;':",.<>?/\\`~]/.test(password)) {
    return {
      valid: false,
      message: "Password must contain at least one number or symbol",
    };
  }

  return { valid: true, message: "" };
}

/**
 * Signup page with email/password form
 * - Custom form (not Cognito Hosted UI)
 * - Client-side password validation (8+ chars, letter + number/symbol)
 * - Navigate to verify-email on CONFIRM_SIGN_UP step
 */
export function SignupPage(): React.ReactElement {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuthState();
  const { signUp } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password strength
    const validation = validatePassword(password);
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signUp(email, password);

      if (result.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        // Navigate to verification page with email for context
        navigate("/verify-email", { state: { email } });
      } else if (result.isSignUpComplete) {
        // Auto sign-in succeeded
        navigate("/", { replace: true });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign up";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-secondary text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-center mb-6 text-text-primary">
          Create your account
        </h1>

        {error && (
          <div className="alert-error mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-field"
            />
          </div>

          <div className="mb-3">
            <label
              htmlFor="password"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="input-field"
            />
            <p className="mt-1.5 text-xs text-text-tertiary">
              At least 8 characters with a letter and number/symbol
            </p>
          </div>

          <div className="mb-4">
            <label
              htmlFor="confirmPassword"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="input-field"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary-lg w-full"
          >
            {isSubmitting ? "Creating account..." : "Create Account"}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-text-secondary">
          Already have an account?{" "}
          <Link to="/login" className="link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
